import React, { memo, useRef } from 'react'

import { FiltersSidebar, Icon, Search } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, func, object, string } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import getArrayFilters from 'Utils/getArrayFilters'
import getFileTypeSlug from 'Utils/getFileTypeSlug'

import FiltersWrapperWithTags from '../FiltersWrapperWithTags/FiltersWrapperWithTags'

function FileFormats({
  currentSelectedFilters,
  currentlySelectedFiltersObjects,
  setSelectedFilters,
  currentFilters,
  setSearchEnchantmentAction,
  selectedFilters,
  filtersOptions,
  trackFilterChange,
  locale = 'en',
}) {
  const searchRef = useRef(null)
  const searchPlaceholder = i18n.t('listingPage.filters.searchPlaceholder', { ns: 'rails', lng: locale })

  function handleFilterChange(selectedValues, value, isOn, resetAll) {
    const truthyValues = selectedValues.filter(Boolean)
    const isFilterSelected = value && isOn

    if (resetAll) {
      const selectedFileTypeFilters = selectedFilters.fileTypes || []

      setSearchEnchantmentAction({
        type: 'remove_filter',
        value: selectedFileTypeFilters.map(item => getFileTypeSlug(item, filtersOptions)).join(','),
      })
      setSelectedFilters({
        name: 'fileTypes',
        value: [...new Set(truthyValues)],
        skipGetLastSelectedFilters: true,
      })
      window.removeCurrentSearchFilter({ name: 'file types' })

      return
    }

    const { filters } = currentFilters
    const matchingFilter = filters
      .find(({ value: filterValue }) => filterValue === value)
    const { label } = matchingFilter || {}

    if (isFilterSelected) {
      if (matchingFilter) {
        trackFilterChange({ name: 'file types', value: label })
      }
    } else {
      window.removeCurrentSearchFilter({ name: 'file types', value: label })
    }

    setSelectedFilters({ name: 'fileTypes', value: [...new Set(truthyValues)] })
  }

  function onOpenChange(isOpen) {
    if (isOpen) {
      setTimeout(() => {
        searchRef.current.focus()
      }, 300)
    }
    return null
  }

  function getContent() {
    const cleanedFilters = {
      ...currentFilters,
      label: null,
      filters: currentFilters.filters?.map(filter => ({
        ...filter,
        label: cleanedFiltersLabels(filter.label),
      })),
    }

    return (
      <FiltersSidebar
        isCollapsible={false}
        filters={[cleanedFilters]}
        selectedFilters={currentSelectedFilters}
        onFiltersChange={handleFilterChange}
        limit={8}
        isControlled
        hideLastDivider
        useNewCheckboxes
        hideShowMore
        searchPlaceholder={searchPlaceholder}
        textInputProps={{
          leadIcon: <Icon notSpaced svgComponent={Search} color="dark" />,
          wrapperClassName: 'file-formats__search',
          textInputClassName: 'file-formats__search-input',
          ref: searchRef,
        }}
        baseFormWrapperClassName="file-formats"
      />
    )
  }

  function cleanLabel(label) {
    const regex = /\(([^)]+)\)/
    const newLabel = label.match(regex)[1]
    const finalLabel = newLabel.replace(/\./g, '')

    return finalLabel
  }

  function cleanedFiltersLabels(label) {
    return label
      .replace(/,\s*,/g, ',')
      .replace(/,\s*\)/g, ')')
      .replace(/\(\s*,/g, '(')
      .replace(/,\s*,\s*/g, ', ')
  }


  return (
    <FiltersWrapperWithTags
      currentlySelectedFiltersObjects={currentlySelectedFiltersObjects || []}
      placeholder={currentFilters.label}
      type="file-formats"
      content={getContent()}
      handleFilterChange={handleFilterChange}
      functionToCleanLabel={cleanLabel}
      tagClassName="to-uppercase"
      onOpenChange={onOpenChange}
    />
  )
}

FileFormats.propTypes = {
  currentSelectedFilters: array.isRequired,
  currentlySelectedFiltersObjects: array.isRequired,
  setSelectedFilters: func.isRequired,
  currentFilters: object.isRequired,
  setSearchEnchantmentAction: func.isRequired,
  selectedFilters: object.isRequired,
  filtersOptions: object.isRequired,
  trackFilterChange: func.isRequired,
  locale: string,
}

const mapStateToProps = (
  {
    selectedFilters,
    setSelectedFilters,
    filtersOptions,
    setSearchEnchantmentAction,
    trackFilterChange,
    locale,
  },
) => {
  const currentFilters = filtersOptions.fileTypes
  const groupsSelectedFilters = selectedFilters.fileTypes || []
  const currentSelectedFilters = getSelectedFilters(groupsSelectedFilters)
  const groupsFilters = currentFilters.filters || []
  const currentlySelectedFiltersObjects = getSelectedFiltersObjects(currentSelectedFilters)

  function getSelectedFilters(filters = []) {
    return getArrayFilters(filters)
  }

  function getSelectedFiltersObjects(filters = []) {
    return filters.map((filter) => {
      const filterObject = groupsFilters.find(({ value }) => value === filter)

      return filterObject || {}
    })
  }

  return {
    currentSelectedFilters,
    currentlySelectedFiltersObjects,
    setSelectedFilters,
    currentFilters: {
      ...currentFilters,
      filters: groupsFilters,
    },
    setSearchEnchantmentAction,
    selectedFilters,
    filtersOptions,
    trackFilterChange,
    locale,
  }
}

const shouldComponentAvoidRender = (prevProps, newProps) => {
  const {
    currentSelectedFilters: prevSelectedFilters,
    currentFilters: prevFilters,
  } = prevProps

  const {
    currentSelectedFilters: newSelectedFilters,
    currentFilters: newFilters,
  } = newProps

  function getRaw(entry) {
    return JSON.stringify(entry)
  }

  const prevCounts = prevFilters.filters
    .map(({ count }) => count)
    .filter(Number)

  const newCounts = newFilters.filters
    .map(({ count }) => count)
    .filter(Number)

  const isLengthSame = prevSelectedFilters.length === newSelectedFilters.length

  const isValueSame = getRaw(prevSelectedFilters) === getRaw(newSelectedFilters)

  const areCountsSame = getRaw(prevCounts) === getRaw(newCounts)

  if (isValueSame && isLengthSame && areCountsSame) {
    return true
  }

  return false
}

export default WithTriangleUiContext(mapStateToProps)(
  memo(FileFormats, shouldComponentAvoidRender)
)
